import { useLocation, Navigate } from "react-router-dom";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { Layout } from "@components/Layout";
import { Loader } from "@components/Loader";
import { FallbackLanguageWarning } from "@components/FallbackLanguageWarning";
import { languageDutch, languageEnglish } from "../i18n";
import { useStoryblokProxy } from "@hooks/useStoryblokProxy";
import { PageBody } from "../storyblok/PageBody";
import { getPageSlugFromPathname } from "../utils/getPageSlugFromPathname";
import { isStoryTranslated } from "../utils/isStoryTranslated";
import { getLanguageFromPathname } from "@utils/slugUtils";
import type { ISbStoryData, SbBlokData } from "@storyblok/react";
import { BannerBlock } from "../types/BannerTypes";
import { Banner } from "../storyblok/Banner";
import { useCurrentLang } from "@hooks/useCurrentLang";
import { useEditorPreviewFallback } from "@hooks/useEditorPreviewFallback";
import { DepartmentPage } from "@pages/DepartmentPage";
import type { DepartmentStory } from "../types/DepartmentTypes";
import { buildDepartmentHref } from "@utils/departmentPaths";
import { Department } from "../constants";

interface GeneralPageContent {
  component: string;
  body: SbBlokData[];
  image?: {
    filename: string;
    focus?: string;
  };
  subtitle?: React.ReactNode;
}

interface GeneralPageStory extends ISbStoryData {
  content: GeneralPageContent;
}

interface DepartmentNavigationState {
  departmentStory?: DepartmentStory;
}

export const GeneralPage = () => {
  const location = useLocation();
  const currentLang = useCurrentLang(
    getLanguageFromPathname(location.pathname)
  );

  const pageSlug = getPageSlugFromPathname(location.pathname);

  const { data, isLoading, isError } = useStoryblokProxy({
    slug: pageSlug,
    language: currentLang === languageEnglish ? languageEnglish : undefined,
  });

  const navigationStory = (location.state as DepartmentNavigationState | null)
    ?.departmentStory;
  const matchingNavigationStory =
    navigationStory &&
    buildDepartmentHref(navigationStory, currentLang) === location.pathname
      ? navigationStory
      : undefined;

  // Internal Department navigation already has the complete target story from
  // the sibling collection. Use it until the exact-story query resolves so the
  // current layout does not disappear behind the GeneralPage loader.
  const story = (data?.story ?? matchingNavigationStory) as
    | GeneralPageStory
    | DepartmentStory
    | undefined;

  // Inside the Visual Editor, keep the editor in context with a clear preview-unavailable
  // message rather than redirecting to /404 (which would drop the signed `_storyblok_tk`
  // params and break the editor session). Non-editor visitors keep the normal 404 redirect.
  const previewFallback = useEditorPreviewFallback(!isLoading && isError);
  if (previewFallback) return previewFallback;

  if (isError)
    return (
      <Navigate to={currentLang === languageEnglish ? "/en/404" : "/404"} />
    );

  // If the story is a Department, we delegate to the DepartmentPage component.
  if (story?.content?.component === Department) {
    return <DepartmentPage story={story as DepartmentStory} />;
  }

  const generalStory = story as GeneralPageStory | undefined;
  const bannerImage =
    !isLoading && generalStory?.content.image?.filename
      ? generalStory.content.image
      : undefined;

  const bannerContent = bannerImage?.filename
    ? ({
        title: generalStory?.name,
        bannerContent: {
          image: bannerImage,
          subtitle: generalStory?.content?.subtitle,
        },
      } as BannerBlock)
    : undefined;

  return (
    <Layout
      translatedSlug={
        currentLang === languageDutch
          ? `/${languageEnglish}/${
              generalStory?.translated_slugs?.find(
                (obj) => obj.lang === languageEnglish
              )?.path
            }`
          : `/${generalStory?.default_full_slug}`
      }
      className="mb-24"
    >
      {isLoading || !generalStory ? (
        <Loader />
      ) : (
        <>
          {bannerContent && (
            <Banner title={generalStory.name} data={bannerContent} />
          )}
          <title>{generalStory.name}</title>

          <article className="mx-auto mt-16 mb-16 w-full max-w-2xl max-md:px-7 md:mb-32">
            {/* If the story isn't translated, we show an alert and the Dutch data. */}
            {!isStoryTranslated(generalStory) &&
              currentLang === languageEnglish && <FallbackLanguageWarning />}

            {!bannerContent && (
              <Typography
                as="h1"
                size="3xl"
                className="mb-6 text-secondary-700 md:mb-4 dark:text-white"
              >
                {generalStory.name}
              </Typography>
            )}
            <PageBody blok={generalStory.content.body} />
          </article>
        </>
      )}
    </Layout>
  );
};
